import React, { useState, useEffect } from "react";
import { Tree } from "antd";
import axios from "axios";
import def from "ajv/dist/vocabularies/applicator/additionalItems";

function DeptTree({
  treeData,
  onUserSelect,
  resetTrigger,
  defaultCheckedUsers = [],
  checkBoxTrigger,
}) {
  const [data, setData] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [loadedKeys, setLoadedKeys] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (treeData && treeData.length > 0) {
      const formatted = buildDeptTree(treeData);
      setData(formatted);
    }
  }, [treeData]);

  useEffect(() => {
    if (resetTrigger) {
      const formatted = buildDeptTree(treeData);
      setData(formatted);
      setCheckedKeys([]);
      setExpandedKeys([]);
      setLoadedKeys([]);
      setInitialized(false);
      if (onUserSelect) {
        onUserSelect([]);
      }
    }
  }, [resetTrigger]);

  useEffect(() => {
    const keys = defaultCheckedUsers.map((user) => `user-${user.MEMBER_ID}`);
    setCheckedKeys([]);
    setCheckedKeys(keys);
    setInitialized(true);
  }, [checkBoxTrigger]);

  useEffect(() => {
    if (!initialized && defaultCheckedUsers.length > 0) {
      const keys = defaultCheckedUsers.map((user) => `user-${user.MEMBER_ID}`);
      setCheckedKeys(keys);
      setInitialized(true);
    }
  }, [defaultCheckedUsers, initialized]);

  const buildDeptTree = (list) => {
    const map = {};
    const roots = [];

    list.forEach((item) => {
      const node = {
        title: item.DEPT_NM,
        key: item.id,
        children: [],
        isLeaf: false,
        disableCheckbox: true,
        original: item,
      };
      map[item.id] = node;
    });

    list.forEach((item) => {
      const node = map[item.id];
      if (item.PAR_CD && item.PAR_CD !== "" && map[item.PAR_CD]) {
        map[item.PAR_CD].children.push(node);
      } else {
        roots.push(node);
      }
    });

    return roots;
  };

  const removeUsersFromNode = (nodes, targetKey) => {
    return nodes.map((node) => {
      if (node.key === targetKey) {
        const filteredChildren = node.children?.filter(
          (child) => !child.key.startsWith("user-")
        );
        return {
          ...node,
          children: filteredChildren || [],
        };
      } else if (node.children && node.children.length > 0) {
        return {
          ...node,
          children: removeUsersFromNode(node.children, targetKey),
        };
      } else {
        return node;
      }
    });
  };

  const onLoadData = (treeNode) => {
    const deptId = treeNode.key;

    if (loadedKeys.includes(deptId)) {
      return Promise.resolve();
    }

    return axios
      .get("/address/usersDept", {
        params: { deptId },
      })
      .then((res) => {
        const users = res.data;

        const userNodes = users.map((user) => ({
          title: user.DISPLAY_NM,
          key: `user-${user.MEMBER_ID}`,
          isLeaf: true,
          data: user,
          disableCheckbox: false,
        }));

        const addUserNodes = (nodes) => {
          return nodes.map((node) => {
            if (node.key === deptId) {
              return {
                ...node,
                children: [...node.children, ...userNodes],
              };
            } else if (node.children && node.children.length > 0) {
              return {
                ...node,
                children: addUserNodes(node.children),
              };
            } else {
              return node;
            }
          });
        };

        const updatedData = addUserNodes(data);
        setData(updatedData);
        setLoadedKeys((prev) => [...prev, deptId]);
      });
  };

  const onExpand = (keys, { expanded, node }) => {
    const deptId = node.key;

    if (expanded) {
      setExpandedKeys(keys);
      if (!loadedKeys.includes(deptId)) {
        onLoadData(node);
      }
    } else {
      const cleaned = removeUsersFromNode(data, deptId);
      setData(cleaned);
      setLoadedKeys((prev) => prev.filter((k) => k !== deptId));
      setExpandedKeys(keys);
    }
  };

  const onCheck = (checkedKeysValue, info) => {
    setCheckedKeys(checkedKeysValue);

    if (info.node?.isLeaf && info.node?.data && onUserSelect) {
      const isNowChecked = !info.checked;
      onUserSelect([
        {
          ...info.node.data,
          isChecked: isNowChecked,
        },
      ]);
    }
  };

  return (
    <Tree
      checkable
      treeData={data}
      expandedKeys={expandedKeys}
      onExpand={onExpand}
      loadData={onLoadData}
      checkedKeys={checkedKeys}
      onCheck={onCheck}
      showLine={{ showLeafIcon: false }}
    />
  );
}

export default DeptTree;
